import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Home as HomeIcon, Search, Crown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import PublicLayout from "@/components/PublicLayout";
import Testimonials from "@/components/sections/Testimonials";
import About from "@/components/sections/About";

const OWNER_IMG =
  "https://images.unsplash.com/photo-1583295125721-766a0088cd3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxhbXN0ZXJkYW0lMjBhcGFydG1lbnR8ZW58MHx8fHwxNzg3MjU4NzU4fDA&ixlib=rb-4.1.0&q=85";
const TENANT_IMG =
  "https://images.unsplash.com/photo-1758523670960-eb5512da12e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGZhbWlseSUyMG5ldyUyMGhvbWUlMjBtb3ZpbmclMjBrZXlzfGVufDB8fHx8MTc4NzU5OTgwNnww&ixlib=rb-4.1.0&q=85";
const CLUB_IMG =
  "https://images.unsplash.com/photo-1675716921224-e087a0cca69a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMG5ldHdvcmtpbmclMjBldmVudCUyMHByb2Zlc3Npb25hbHMlMjBtZWV0aW5nfGVufDB8fHx8MTc5MTIwNDkyMHww&ixlib=rb-4.1.0&q=85";

function ChoiceCard({ to, image, icon: Icon, title, desc, highlight, cta, note, delay, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 240, damping: 24, delay }}
    >
      <Link
        to={to}
        data-testid={testId}
        className="group relative block overflow-hidden rounded-3xl border border-line bg-ink shadow-sm hover:shadow-2xl transition-shadow h-[440px] md:h-[520px]"
      >
        <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70 group-hover:scale-105 group-hover:opacity-80 transition-[transform,opacity] duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/10" />

        <div className="relative z-10 h-full flex flex-col justify-between p-7 md:p-9">
          <div className="flex items-center justify-between">
            <span className="h-12 w-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center text-white">
              <Icon size={24} strokeWidth={2} />
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3.5 py-1.5 text-xs font-bold text-white">
              <Check size={13} strokeWidth={3} /> {highlight}
            </span>
          </div>

          <div>
            <h2 className="font-serif font-semibold text-white text-3xl md:text-4xl leading-tight tracking-tight">{title}</h2>
            <p className="mt-3 text-white/75 text-sm md:text-base leading-relaxed max-w-sm">{desc}</p>
            <div className="mt-6">
              <span className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-brand text-white text-sm md:text-base font-semibold leading-tight group-hover:bg-brandhover shadow-lg transition-colors">
                {cta} <ArrowRight size={18} className="shrink-0 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="mt-3 text-xs text-white/60">{note}</div>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Home() {
  const { t } = useLang();
  const c = t.chooser;

  return (
    <PublicLayout variant="home">
      <section id="chooser" className="bg-mist pt-28 pb-20 md:pt-36 md:pb-28 min-h-screen scroll-mt-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-line bg-paper px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand">
              {c.badge}
            </span>
            <h1 className="mt-6 font-serif font-semibold text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05]">
              {c.title}
            </h1>
            <p className="mt-5 text-stone text-base md:text-lg leading-relaxed max-w-2xl mx-auto">{c.subtitle}</p>
          </motion.div>

          <div className="mt-12 grid gap-5 md:gap-6 md:grid-cols-3">
            <ChoiceCard
              to="/ev-sahipleri-klubu" image={CLUB_IMG} icon={Crown}
              title={c.club.title} desc={c.club.desc} highlight={c.club.highlight}
              cta={c.club.cta} note={c.club.note} delay={0.1} testId="choose-club"
            />
            <ChoiceCard
              to="/ev-sahibi" image={OWNER_IMG} icon={HomeIcon}
              title={c.owner.title} desc={c.owner.desc} highlight={c.owner.highlight}
              cta={c.owner.cta} note={c.owner.note} delay={0.2} testId="choose-owner"
            />
            <ChoiceCard
              to="/kiraci" image={TENANT_IMG} icon={Search}
              title={c.tenant.title} desc={c.tenant.desc} highlight={c.tenant.highlight}
              cta={c.tenant.cta} note={c.tenant.note} delay={0.3} testId="choose-tenant"
            />
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3"
          >
            {c.trust.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm font-medium text-ink/80">
                <Check size={16} strokeWidth={2.5} className="text-moss" /> {item}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <Testimonials />
      <About />
    </PublicLayout>
  );
}
